import { AnimatePresence, motion } from 'framer-motion'
import { type SyntheticEvent, useState } from 'react'
import { useTheme } from 'styled-components'
import * as Accordion from '@radix-ui/react-accordion'
import type { MenuItem } from '@velasca/types'
import { IconButton } from '@velasca/ui/icon-button'
import { Link } from '../MobileNavigation.styles'
import {
  AccordionHeader,
  AccordionTrigger,
  DisclosurePanel,
  FirstLevelMenu,
  FirstLevelMenuTitle,
  Hr,
  SecondLevelMenu,
  SecondLevelMenuTitle,
  StyledArrowDownIcon,
  ThirdLevelMenuTitle,
} from './AccordionItem.styles'

interface AccordionItemProps {
  menu: MenuItem
}

const handleLinkClick = (e: SyntheticEvent<HTMLDivElement>) => {
  e.stopPropagation()
}

export const AccordionItem = ({ menu }: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleContainerClick = () => {
    setIsOpen(!isOpen)
  }

  const theme = useTheme()

  return (
    <Accordion.Item asChild value={menu.title} onClick={handleContainerClick}>
      <li>
        <AccordionHeader>
          <AccordionTrigger>
            <FirstLevelMenu
              selected={isOpen || menu?.current}
              $hasChildren={Boolean(menu?.children)}
            >
              {menu?.children ? (
                <>
                  <FirstLevelMenuTitle isExpanded={isOpen}>
                    {menu?.title}
                  </FirstLevelMenuTitle>
                  <IconButton
                    iconSize="1.25rem"
                    clickAreaSize="3.2rem"
                    color={
                      isOpen ? theme.colors.primary : theme.colors.text.primary
                    }
                  >
                    <StyledArrowDownIcon $rotate={isOpen} />
                  </IconButton>
                </>
              ) : (
                <Link
                  href={menu?.url}
                  target={menu?.isTargetBlank ? '_blank' : '_self'}
                >
                  <FirstLevelMenuTitle>{menu?.title}</FirstLevelMenuTitle>
                </Link>
              )}
            </FirstLevelMenu>
            <Hr />
          </AccordionTrigger>
        </AccordionHeader>
        <Accordion.Content onClick={handleLinkClick}>
          {menu?.children && (
            <AnimatePresence custom={isOpen}>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, transition: { duration: 0 } }}
                  transition={{ duration: 0.3 }}
                >
                  <DisclosurePanel $hasChildren={Boolean(menu?.children)}>
                    {menu?.children?.map((child, i) => {
                      return (
                        <SecondLevelMenu key={`second-level-${i}`}>
                          <Link
                            href={child.url}
                            target={child.isTargetBlank ? '_blank' : '_self'}
                          >
                            <SecondLevelMenuTitle active={child.current}>
                              {child.title}
                            </SecondLevelMenuTitle>
                          </Link>
                          {child.children?.map((grandChild, index) => {
                            return (
                              <Link
                                key={`third-level-${index}`}
                                href={grandChild?.url}
                                target={
                                  grandChild?.isTargetBlank ? '_blank' : '_self'
                                }
                              >
                                <ThirdLevelMenuTitle
                                  active={grandChild?.current}
                                >
                                  {grandChild?.title}
                                </ThirdLevelMenuTitle>
                              </Link>
                            )
                          })}
                        </SecondLevelMenu>
                      )
                    })}
                  </DisclosurePanel>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </Accordion.Content>
      </li>
    </Accordion.Item>
  )
}
